import { HttpParams } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Permissions } from '../../common/constants';
import { ACTION_DEFINITION_SPARE_PART_REFERENCES, USER_ACTIONS_EXPORT, USER_ACTIONS_V2, WORK_ORDERS, WORK_ORDERS_V2 } from '../../common/endpoints';
import { ActionDefinition, ActionDefinitionSparePartReference, ActionStatus, ActionTopic, PagedList, WorkOrder } from '../../model';
import { ActionDefinitionService } from '../../service/action-definition.service';
import { AuthenticationService } from '../../service/authentication.service';
import { CustomPropertyService } from '../../service/custom-property.service';
import { DownloadingObject, DownloadService } from '../../service/download.service';
import { HttpService } from '../../service/http.service';
import { UserActionStatusService } from '../../service/user-action-status.service';
import { WorkOrderService } from '../../service/work-order.service';
import { ActionAdvancedSearchComponent } from '../../shared/advanced-search/action-advanced-search/action-advanced-search.component';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { PropertyComponent } from '../../shared/component';
import { CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { DownloadStatus, DownloadType } from '../../shared/download-dialog/download-dialog.component';
import { AbstractListWidgetV2Service } from '../../shared/list-widget-v2/abstract-list-widget-v2.service';
import { ActionListWidgetService } from '../action-list/action-list-widget.service';

@Injectable()
export class ActionListWidgetV2Service extends ActionListWidgetService {

    private active: boolean;
    private showWorkOrders: boolean;
    private visibleColumnFieldNames: string[];
    private visibleColumnFieldLabels: string[];
    private defaultProperties = ActionAdvancedSearchComponent.DEFAULT_SEARCHABLE_PROPERTIES;
    private workOrderId: string;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => CustomPropertyService)) customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => AbstractContextService)) protected contextService: AbstractContextService,
        @Inject(forwardRef(() => AbstractThingContextService)) protected thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => HttpService)) protected httpService: HttpService,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => ActionDefinitionService)) private actionDefinitionService: ActionDefinitionService,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => WorkOrderService)) private workOrderService: WorkOrderService,
    ) {
        super(authenticationService, customPropertyService, contextService, thingContextService, httpService);
        this.showWorkOrders = this.authenticationService.hasPermission(Permissions.READ_WORK_ORDER);
    }

    setActive(active: boolean): void {
        this.active = active;
    }

    setWorkOrderId(id: string): void {
        this.workOrderId = id;
    }

    getPagedList(page: number, size: number, sort?: string[], searchFields?: string[], advancedSearchBody?: any, workOrderId?: string): Promise<PagedList<ActionStatus>> {
        let params = this.getSearchParams(searchFields, advancedSearchBody, workOrderId, false);
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        if (this.active || workOrderId) {
            if (sort && sort[0]) {
                if (sort[0].startsWith(AbstractListWidgetV2Service.COMPOSITE_SORT_PREFIX)) {
                    sort = this.assignCompositeSort(sort);
                }
                params = params.set('sort', sort.join(','));
            }
        }

        return this.userActionStatusService.getPagedList(params);
    }

    getRecursivelyAllStatuses(page?: number, statuses?: ActionStatus[], advancedSearchBody?: any): Promise<ActionStatus[]> {
        if (!page) {
            page = 0;
        }
        if (!statuses) {
            statuses = [];
        }
        return this.getPagedList(page, 100, null, null, advancedSearchBody)
            .then(pagedMetrics => {
                statuses = statuses.concat(pagedMetrics.content);
                if (pagedMetrics.last) {
                    return statuses;
                } else {
                    return this.getRecursivelyAllStatuses(++page, statuses, advancedSearchBody);
                }
            });
    }

    getSearchParams(searchFields: string[], advancedSearchBody: any, workOrderId: string, forExport: boolean): HttpParams {
        let params = this.getParams(searchFields, advancedSearchBody, forExport, workOrderId);
        params = params.set('active', this.active);
        params = params.set('language', this.authenticationService.getUser()?.language || navigator.language);
        if (workOrderId) {
            params = params.set('workOrderId', `eq;${workOrderId}`);
        }
        return params;
    }

    private getParams(searchFields: string[], advancedSearchBody: any, forExport: boolean, workOrderId?: string): HttpParams {
        let params = new HttpParams();
        const locationId = this.contextService.getCurrentLocation()?.id || this.authenticationService.getUser().locationId;
        const customerId = this.contextService.getCurrentCustomer()?.id || this.authenticationService.getUser().customerId;
        if (this.thingContextService.getCurrentThing()) {
            params = params.set('thingId', this.thingContextService.getCurrentThing().id);
        } else if (locationId) {
            params = params.set('locationId', locationId);
        } else if (customerId) {
            params = params.set('customerId', customerId);
        }
        if (advancedSearchBody) {
            if (advancedSearchBody.key) {
                params = params.set('searchText', "*" + advancedSearchBody.key + "*");
                searchFields.forEach(field => params = params.append('searchField', field));
            }
            if (advancedSearchBody.topics && advancedSearchBody.topics.length) {
                advancedSearchBody.topics.forEach(topic => params = params.append('topic', topic));
            }
            if (advancedSearchBody.actionDefinitions && advancedSearchBody.actionDefinitions.length) {
                advancedSearchBody.actionDefinitions.forEach(actionDefinitionId => params = params.append('actionDefinitionId', actionDefinitionId));
            }
            if (advancedSearchBody.priorities && advancedSearchBody.priorities.length) {
                advancedSearchBody.priorities.forEach(priority => params = params.append('priority', priority));
            }
            if (advancedSearchBody.statuses && advancedSearchBody.statuses.length) {
                advancedSearchBody.statuses.forEach(state => params = params.append('state', state));
            }
            if (advancedSearchBody.selectedThingIds && advancedSearchBody.selectedThingIds.length) {
                advancedSearchBody.selectedThingIds.forEach(id => params = params.append('selectedThingId', id));
            }
            if (advancedSearchBody.startTimestamp) {
                params = params.set('minStartTimestamp', advancedSearchBody.startTimestamp);
            }
            if (advancedSearchBody.endTimestamp) {
                params = params.set('maxStartTimestamp', advancedSearchBody.endTimestamp);
            }
            if (advancedSearchBody.actionDefinitionTypes && advancedSearchBody.actionDefinitionTypes.length) {
                advancedSearchBody.actionDefinitionTypes.forEach(type => params = params.append('actionDefinitionType', type));
            }
            const defaultProps = this.defaultProperties.filter(p => advancedSearchBody[p.name]);
            defaultProps.forEach(property => params = params.set(property.name, advancedSearchBody[property.name]));
            const properties = Object.keys(advancedSearchBody).filter(key => key.includes('properties.'));
            properties.forEach(property => params = params.set(property, advancedSearchBody[property]));
        }
        if (forExport) {
            this.filterVisibleExportFields(workOrderId);
            if (this.visibleColumnFieldNames) {
                this.visibleColumnFieldNames.forEach(columnFieldName => params = params.append('selectField', columnFieldName));
            }
            if (this.visibleColumnFieldLabels) {
                params = params.append('labels', this.visibleColumnFieldLabels.join(';'));
            }
        }
        return params;
    }

    private filterVisibleExportFields(workOrderId: string): void {
        this.visibleColumnFieldNames = [];
        this.visibleColumnFieldLabels = [];
        if ((this.active && !this.showWorkOrders)) {
            this.filterWorkOrderExportFields();
        } else {
            if (!workOrderId) {
                this.filterWorkOrderExportFields();
            } else {
                if (this.columnFieldNames) {
                    this.visibleColumnFieldNames.push(...this.columnFieldNames);
                }
                if (this.columnFieldLabels) {
                    this.visibleColumnFieldLabels.push(...this.columnFieldLabels);
                }
                if (!this.columnFieldNames?.includes('discardReason.code')) {
                    this.visibleColumnFieldNames.push('discardReason.code');
                    this.visibleColumnFieldLabels.push('discardReasonCodeProperty');
                }
            }
            this.visibleColumnFieldNames.push(this.active ? 'executionState' : 'state');
            this.visibleColumnFieldLabels.push('executionProperty');
        }
    }

    private filterWorkOrderExportFields(): void {
        this.columnFieldNames?.forEach((fieldName, index) => {
            if (!fieldName.startsWith('workOrder')) {
                this.visibleColumnFieldNames.push(fieldName);
                const fieldLabel = this.columnFieldLabels && this.columnFieldLabels[index] ? this.columnFieldLabels[index] : fieldName;
                this.visibleColumnFieldLabels.push(fieldLabel);
            }
        });

    }

    protected isColumnVisible(columnName: string): boolean {
        if (this.active) {
            return columnName != 'duration';
        }
        return true;
    }

    protected getPropertyColumn(col: PropertyComponent, columnName: string, defaultType: string): CustomTableColumn {
        const sortEnabled = this.active || this.workOrderId;
        switch (col.name) {
            case 'customer.name':
                return CustomTableService.newCustomerLinkColumn(this.getLabel(col, defaultType), 'customer', 'customerId').withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'location.name':
                return CustomTableService.newLocationLinkColumn(this.getLabel(col, defaultType), 'location', 'locationId').withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.serviceLevel':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), 'serviceLevel', this.getFilter(col, defaultType)).withSortField(sortEnabled ? 'serviceLevel.level' : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'location.country':
            case 'location.timezone':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType))
                    .withAlternativePath(col.name.replace('location', 'customer')).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.gpsPosition':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), 'gpsPosition', this.getFilter(col, defaultType))
                    .withAlternativePath('location.gpsPosition').withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.tag':
                return CustomTableService.newTagColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.parentThingId':
                return CustomTableService.newParentThingColumn(this.getLabel(col, defaultType), columnName).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'startTimestamp':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), 'startTimestamp', this.getFilter(col, defaultType)).withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'icon':
                return CustomTableService.newImageColumn(columnName, '', 'actionDefinition.iconUrl').withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.tags':
                return CustomTableService.newTagColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'topic':
                return CustomTableService.newIconColumn(columnName, '', 'actionDefinition.topic', this.getTopicIconMap()).withStyle({ '_any': { 'padding': '0px' } });
            case 'thing.name':
                return CustomTableService.newThingLinkColumn('thingName', this.getLabel(col, defaultType), 'thing', 'name').withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.serialNumber':
                return CustomTableService.newThingLinkColumn('thingSerialNumber', this.getLabel(col, defaultType), 'thing', 'serialNumber').withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'customer.partner.name':
            case 'location.partner.name':
            case 'thing.partner.name':
                return CustomTableService.newPartnerLinkColumn(this.getLabel(col, defaultType), col.name.substring(0, (col.name.length - ('name'.length) - 1))).withSortField(sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'actionDefinition.type.label':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withArgument(null, false, 'action').withLocalization()
                    .withSortField(sortEnabled ? col.name : null).withValueMap(this.getValueMap(col)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'workTime':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType))
                    .withSortField(sortEnabled ? columnName : null).withValueMap(this.getValueMap(col)).withColumnClass('align-content-right').withShowHeader(col.showHeader);
            case 'workOrder.code':
                return CustomTableService.newWorkOrderLinkColumn(this.getLabel(col, defaultType), col.name).withSortField(sortEnabled ? col.name : null);
            default:
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withArgument(this.getArgument(col), false, 'action')
                    .withSortField(sortEnabled ? col.name : null).withValueMap(this.getValueMap(col)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
        }
    }

    getActionDefinitionPagedList(page: number, size: number, sort: string[], advancedSearchBody: any, topics: ActionTopic[]): Promise<PagedList<ActionDefinition>> {
        let params = new HttpParams().set('activationType', 'MANUAL');
        params = params.set('includeInherited', true);
        if (advancedSearchBody?.key) {
            params = params.set('searchText', "*" + advancedSearchBody.key + "*");
        }
        if (topics?.length) {
            topics.forEach(t => params = params.append('topic', t));
        }
        params = params.set('thingDefinitionId', this.thingContextService.getCurrentThing().thingDefinitionId);
        return this.actionDefinitionService.getPagedActionDefinitions(page, size, sort, params);
    }

    getWorkOrderPagedList(page: number, size: number, sort: string[], thingId: string, active: boolean, advancedSearchBody?: any): Promise<PagedList<WorkOrder>> {
        let params = new HttpParams().set('thingId', thingId);
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        if (sort && sort[0]) {
            params = params.set('sort', sort.join(','));
        }
        params = params.set('active', active);
        if (advancedSearchBody?.key) {
            params = params.set('searchText', "*" + advancedSearchBody.key + "*");
        }
        return firstValueFrom(this.httpService.get<PagedList<WorkOrder>>(WORK_ORDERS_V2, params));
    }

    getRecursivelyAllWorkOrders(thingId: string, active: boolean, page?: number, workOrders?: WorkOrder[]): Promise<WorkOrder[]> {
        if (!page) {
            page = 0;
        }
        if (!workOrders) {
            workOrders = [];
        }
        return this.getWorkOrderPagedList(page, 100, ['name', 'asc'], thingId, active)
            .then(pagedWorkOrders => {
                workOrders = workOrders.concat(pagedWorkOrders.content);
                if (pagedWorkOrders.last) {
                    return workOrders;
                } else {
                    return this.getRecursivelyAllWorkOrders(thingId, active, ++page, workOrders);
                }
            });
    }

    createWorkOrder(actionStatusIds: string[], thingId: string): Promise<WorkOrder> {
        const params = new HttpParams().set('thingId', thingId);
        return firstValueFrom(this.httpService.post<WorkOrder>(WORK_ORDERS, actionStatusIds, params));
    }

    getWorkOrder(id: string): Promise<WorkOrder> {
        return this.workOrderService.getById(id, false);
    }

    bulkAddToWorkOrder(searchFields: string[], advancedSearchBody: any, workOrderId: string, elementIds: string[], selectAll: boolean): Promise<{ count: number }> {
        let params = this.getParams(searchFields, advancedSearchBody, false);
        params = params.set('selectedWorkOrderId', workOrderId);
        params = params.set('selectAll', selectAll);
        const body = { elementIds: elementIds };
        return firstValueFrom(this.httpService.post<{ count: number }>(USER_ACTIONS_V2 + "/bulkAddToWorkOrder", body, params));
    }

    bulkRemoveFromWorkOrder(searchFields: string[], advancedSearchBody: any, workOrderId: string, elementIds: string[], selectAll: boolean): Promise<{ count: number }> {
        let params = this.getParams(searchFields, advancedSearchBody, false);
        params = params.set('selectAll', selectAll);
        if (workOrderId) {
            params = params.set('workOrderId', `eq;${workOrderId}`);
        }
        const body = { elementIds: elementIds };
        return firstValueFrom(this.httpService.post<{ count: number }>(USER_ACTIONS_V2 + "/bulkRemoveFromWorkOrder", body, params));
    }

    bulkMarkAsDiscarded(searchFields: string[], advancedSearchBody: any, workOrderId: string, body: any, selectAll: boolean): Promise<{ count: number }> {
        let params = this.getParams(searchFields, advancedSearchBody, false);
        params = params.set('selectAll', selectAll);
        if (workOrderId) {
            params = params.set('workOrderId', `eq;${workOrderId}`);
        }
        return firstValueFrom(this.httpService.post<{ count: number }>(USER_ACTIONS_V2 + "/bulkMarkAsDiscarded", body, params));
    }

    bulkDeleteWorkOrderNative(searchFields: string[], advancedSearchBody: any, workOrderId: string, elementIds: string[], selectAll: boolean): Promise<{ count: number }> {
        let params = this.getParams(searchFields, advancedSearchBody, false);
        params = params.set('selectAll', selectAll);
        if (workOrderId) {
            params = params.set('workOrderId', `eq;${workOrderId}`);
        }
        const body = { elementIds: elementIds };
        return firstValueFrom(this.httpService.post<{ count: number }>(USER_ACTIONS_V2 + "/bulkDeleteWorkOrderNative", body, params));
    }

    updateWorkOrder(id: string, body: any, isHistorical: boolean): Promise<WorkOrder> {
        return this.workOrderService.updateWorkOrder(id, body, isHistorical);
    }

    getSparePartsFromWorkOrder(workOrder: WorkOrder): Promise<ActionDefinitionSparePartReference[]> {
        let params = new HttpParams();
        params = params.set("workOrderId", workOrder.id);
        params = params.set("thingId", workOrder.thingId);
        params = params.set("active", this.active);
        return firstValueFrom(this.httpService.get<ActionDefinitionSparePartReference[]>(ACTION_DEFINITION_SPARE_PART_REFERENCES, params));
    }

    downloadCSV(params: HttpParams, fileName: string): void {
        const downloadingObject: DownloadingObject = {
            fileName: fileName || 'actions.csv',
            uuid: null,
            status: DownloadStatus.DOWNLOADING,
            type: DownloadType.CSV,
            csvEndpoint: USER_ACTIONS_EXPORT,
            params: params
        }
        this.downloadService.addDownloadingObject(downloadingObject);
        this.downloadService.setVisible();
    }

} 